import { Toast, Toaster, Transition, type ToastData, type ToasterListener } from "terracotta";
import { Text } from "./i18n";
import { createSignal, type JSX, For, createEffect, onCleanup, Match, Switch } from "solid-js";
import type { JoinedMessage, LeftMessage, LogEntry, LogMessage as LogMessageType } from "./message";
import { VsChromeClose } from "solid-icons/vs";

export type ToastMessage = {
  level: "default" | "success" | "warning" | "info" | "error";
  message: string;
};

export class ToasterStore<T> {
  private static toasterID = 0;

  private id: number;

  private queue: ToastData<T>[] = [];

  private listeners = new Set<ToasterListener<T>>();

  private toastID = 0;

  constructor() {
    this.id = ToasterStore.toasterID;
    ToasterStore.toasterID += 1;
  }

  subscribe(callback: ToasterListener<T>): () => void {
    this.listeners.add(callback);
    return () => {
      this.listeners.delete(callback);
    };
  }

  private notify(): void {
    const clone = [...this.queue];
    for (const listener of this.listeners.keys()) {
      listener(clone);
    }
  }

  create(data: T): string {
    const id = `toast-${this.id}-[${this.toastID}`;
    this.toastID += 1;
    this.queue.push({
      id,
      data,
    });
    this.notify();
    return id;
  }

  remove(id: string): void {
    this.queue = this.queue.filter((item) => item.id !== id);
    this.notify();
  }

  update(id: string, data: T): void {
    this.queue = this.queue.map((item) => (item.id === id ? { ...item, data } : item));
    this.notify();
  }

  // Sonner-like convenience API
  message(message: string): string {
    return this.create({ message, level: "default" } as T);
  }

  info(message: string): string {
    return this.create({ message, level: "info" } as T);
  }

  success(message: string): string {
    return this.create({ message, level: "success" } as T);
  }

  warning(message: string): string {
    return this.create({ message, level: "warning" } as T);
  }

  error(message: string): string {
    return this.create({ message, level: "error" } as T);
  }

  promise<R>(
    promise: Promise<R>,
    options: {
      loading: string;
      success: string | ((data: R) => string);
      error: string | ((err: any) => string);
    }
  ): string {
    const id = this.create({ message: options.loading, level: "default" } as T);
    promise
      .then((data) => {
        const successMessage = typeof options.success === "function" ? options.success(data) : options.success;
        this.update(id, { message: successMessage, level: "success" } as T);
      })
      .catch((err) => {
        const errorMessage = typeof options.error === "function" ? options.error(err) : options.error;
        this.update(id, { message: errorMessage, level: "error" } as T);
      });
    return id;
  }

  clear(): void {
    this.queue = [];
    this.notify();
  }

  getQueue(): ToastData<T>[] {
    return this.queue;
  }
}

export function useToaster<T>(toaster: ToasterStore<T>): () => ToastData<T>[] {
  const [signal, setSignal] = createSignal(toaster.getQueue());

  createEffect(() => {
    onCleanup(toaster.subscribe(setSignal));
  });

  return signal;
}

export const notifications = new ToasterStore<ToastMessage | LogMessageType | JoinedMessage | LeftMessage>();

interface LogMessageProps {
  entry: LogEntry;
}

export const LogMessage = (props: LogMessageProps) => {
  if (props.entry.type === "log") {
    if (props.entry.code === 10) {
      return (
        <span classList={{ "text-orange-600": props.entry.level === "warning" }}>
          <Text
            message="{name} reconnected to the room, probably a network issue on their side."
            name={props.entry.name!}
          />
        </span>
      );
    }
    if (props.entry.code === 11) {
      return (
        <span classList={{ "text-orange-600": props.entry.level === "warning" }}>
          <Text message="There was a network issue, you have been reconnected." />
        </span>
      );
    }
    if (props.entry.code === 12) {
      return (
        <span classList={{ "text-orange-600": props.entry.level === "warning" }}>
          <Text message="Connection issue, reconnecting... Try to stop your VPN?" />
        </span>
      );
    }
    if (props.entry.code === 13) {
      return (
        <span classList={{ "text-orange-600": props.entry.level === "warning" }}>
          <Text
            message="This room has reached the maximum number of allowed users. {name} can't join."
            name={props.entry.name!}
          />
        </span>
      );
    }
    if (props.entry.code === 20) {
      return (
        <span classList={{ "text-orange-600": props.entry.level === "warning" }}>
          <Text message="{name} switched automatically to another mic." name={props.entry.name!} />
        </span>
      );
    }
    if (props.entry.code === 21) {
      return (
        <span classList={{ "text-orange-600": props.entry.level === "warning" }}>
          <Text
            message="{name} seems to have an issue with their mic that is temporarily disconnected..."
            name={props.entry.name!}
          />
        </span>
      );
    }
    if (props.entry.code === 22) {
      return (
        <span classList={{ "text-orange-600": props.entry.level === "warning" }}>
          <Text message="{name}'s mic issue seems to be resolved now." name={props.entry.name!} />
        </span>
      );
    }
    if (props.entry.code === 23) {
      return (
        <span classList={{ "text-orange-600": props.entry.level === "warning" }}>
          <Text message="{name} switched to another mic." name={props.entry.name!} />
        </span>
      );
    }
    if (props.entry.code === 24) {
      return (
        <span classList={{ "text-orange-600": props.entry.level === "warning" }}>
          <Text message="Your mic is muted from the tab." />
        </span>
      );
    }
    if (props.entry.code === 25) {
      return (
        <span classList={{ "text-orange-600": props.entry.level === "warning" }}>
          <Text message="The audio has been suspended, click somewhere in the room to resume the audio." />
        </span>
      );
    }
    if (props.entry.code === 26) {
      return (
        <span classList={{ "text-orange-600": props.entry.level === "warning" }}>
          <Text message="The audio is now enabled again." />
        </span>
      );
    }
    if (props.entry.code === 30) {
      return (
        <span classList={{ "text-orange-600": props.entry.level === "warning" }}>
          <Text message="fly mode disabled" />
        </span>
      );
    }
    if (props.entry.code === 31) {
      return (
        <span classList={{ "text-orange-600": props.entry.level === "warning" }}>
          <Text message="fly mode enabled" />
        </span>
      );
    }
    if (props.entry.code === 40) {
      return (
        <span classList={{ "text-orange-600": props.entry.level === "warning" }}>
          <Text message="Hint: You can use drag & drop" />
        </span>
      );
    }
    if (props.entry.code === 50) {
      return (
        <span classList={{ "text-orange-600": props.entry.level === "warning" }}>
          <Text message="Now controlling NPC {name}" name={props.entry.name!} />
        </span>
      );
    }
    if (props.entry.code === 51) {
      return (
        <span classList={{ "text-orange-600": props.entry.level === "warning" }}>
          <Text message="Stopped controlling NPC {name}" name={props.entry.name!} />
        </span>
      );
    }
    if (props.entry.code === 60) {
      return (
        <span classList={{ "text-orange-600": props.entry.level === "warning" }}>
          <Text message="Only the moderator can teleport everyone" />
        </span>
      );
    }
    if (props.entry.code === 61) {
      return (
        <span>
          <Text
            message="{name} muted you. You need to unmute yourself to be able to speak again."
            name={props.entry.name!}
          />
        </span>
      );
    }
    if (props.entry.code === 100) {
      return (
        <span>
          <Text message="Saved" />
        </span>
      );
    }
    if (props.entry.code === 101) {
      return (
        <span class="text-red-600">
          <Text message="Error saving" />
        </span>
      );
    }
    if (props.entry.code === 102) {
      return (
        <span class="max-w-80 text-orange-600">
          <Text message="You don't have the permission to save this scene, all your changes will be lost, be sure to export your changes." />
        </span>
      );
    }
    if (props.entry.code === 103) {
      return (
        <span class="max-w-80 text-red-600">
          <Text message="The room has been modified by another user. Please refresh and try again." />
        </span>
      );
    }

    console.error("Unknown log message", props.entry);
    return null;
  }

  if (props.entry.type === "joined") {
    return (
      <span>
        <Text message="{name} joined the room." name={props.entry.name!} />
      </span>
    );
  }

  if (props.entry.type === "left") {
    return (
      <span>
        <Text message="{name} left." name={props.entry.name!} />
      </span>
    );
  }
};

interface ToastProps {
  id: string;
  entry: LogEntry | ToastMessage;
}

function CustomToast(props: ToastProps) {
  const [isOpen, setIsOpen] = createSignal(true);

  function dismiss() {
    setIsOpen(false);
  }

  return (
    <Transition
      show={isOpen()}
      class="bg-panel rounded-box p-3 transition"
      enter="ease-out duration-300"
      enterFrom="opacity-0 scale-50"
      enterTo="opacity-100 scale-100"
      leave="ease-in duration-200"
      leaveFrom="opacity-100 scale-100"
      leaveTo="opacity-0 scale-50"
      afterLeave={() => {
        notifications.remove(props.id);
      }}
    >
      <Toast class="flex items-center justify-between gap-1">
        <div
          class="flex text-sm font-semibold select-none"
          // TODO better style for warning orange message like mic muted from tab, and it should stay until discarded or resolved, not auto dissapearing
          // classList={{ "text-slate-50": props.entry.level !== "warning", "bg-white": props.entry.level === "warning" }}
        >
          <Switch>
            {/* @ts-ignore */}
            <Match when={!props.entry.type}>
              {/* show an icon based on the level https://github.com/emilkowalski/sonner/blob/main/src/assets.tsx */}
              {/* @ts-ignore */}
              <span classList={{ "text-red-600": props.entry.level === "error" }}>{props.entry.message}</span>
            </Match>
            {/* @ts-ignore */}
            <Match when={props.entry.type}>
              {/* @ts-ignore */}
              <LogMessage entry={props.entry} />
            </Match>
          </Switch>
        </div>
        <button type="button" class="ui-btn ui-btn-soft ui-btn-circle ui-btn-sm" onClick={dismiss}>
          <VsChromeClose size={16} />
        </button>
      </Toast>
    </Transition>
  );
}

export default function Notifications(): JSX.Element {
  const notifs = useToaster(notifications);

  const [isOpen, setIsOpen] = createSignal(false);

  function closeNotifs() {
    setIsOpen(false);
  }

  function clearNotifs() {
    notifications.clear();
  }

  createEffect(() => {
    if (notifs().length > 0) {
      setIsOpen(true);
    }

    const timeout = setTimeout(() => {
      closeNotifs();
    }, 10000);

    onCleanup(() => {
      clearTimeout(timeout);
    });
  });

  return (
    <Toaster>
      <Transition
        show={isOpen()}
        class="relative transition"
        enter="ease-out duration-300"
        enterFrom="opacity-0 scale-50 translate-y-full"
        enterTo="opacity-100 scale-100 translate-y-0"
        leave="ease-in duration-200"
        leaveFrom="opacity-100 scale-100 translate-y-0"
        leaveTo="opacity-0 scale-50  translate-y-full"
        afterLeave={clearNotifs}
      >
        <div class="flex max-h-96 min-w-80 flex-col gap-2 overflow-hidden">
          <div class="rounded-box flex flex-col-reverse gap-1 overflow-y-auto">
            <For
              each={notifs()}
              fallback={
                <div class="bg-panel flex items-center justify-center p-4 font-bold">
                  <Text message="There are no notifications." />
                </div>
              }
            >
              {(item) => <CustomToast id={item.id} entry={item.data} />}
            </For>
          </div>
        </div>
      </Transition>
    </Toaster>
  );
}
